import React from 'react';

import {
  ICanvasOverlay,
  ICanvasOverlayCallback,
} from '../../types/canvasOverlay';
import { CanvasOverlay } from './canvasOverlay';

export interface ICanvasOverlayContainerProps {
  registerForOverlayUpdates: (cb: ICanvasOverlayCallback) => void;
  registerForSiteDimensionsUpdates: (
    cb: (params: {
      isMobileView: boolean;
      width: number;
      height: number;
      siteScale: number;
    }) => void,
  ) => void;
  isResponsiveEditor: boolean;
}

function getResponsiveEditorDimensions() {
  return {
    width: '100%',
  };
}

function getClassicEditorDimensions({
  siteScale,
  siteWidth,
}: {
  siteScale: number;
  siteWidth: number;
}) {
  return {
    width: `${siteWidth * siteScale}px`,
    left: `calc(50% - ${siteWidth * siteScale}px/2)`,
  };
}

export const CanvasOverlayContainer: React.FC<ICanvasOverlayContainerProps> = (
  props: ICanvasOverlayContainerProps,
) => {
  const { registerForOverlayUpdates, registerForSiteDimensionsUpdates } = props;
  const [overlays, setOverlays] = React.useState<ICanvasOverlay[]>([]);
  const [siteHeight, setSiteHeight] = React.useState<number>(0);
  const [siteWidth, setSiteWidth] = React.useState<number>(0);
  const [siteScale, setSiteScale] = React.useState<number>(1);
  const [isMobileView, setIsMobileView] = React.useState<boolean>(false);

  React.useEffect(() => {
    registerForOverlayUpdates((_overlays: ICanvasOverlay[]) => {
      setOverlays(_overlays);
    });
  }, [registerForOverlayUpdates]);

  React.useEffect(() => {
    registerForSiteDimensionsUpdates(
      ({
        width,
        height,
        isMobileView: newIsMobileView,
        siteScale: newSiteScale,
      }) => {
        setSiteHeight(height);
        setSiteWidth(width);
        setIsMobileView(newIsMobileView);
        setSiteScale(newSiteScale);
      },
    );
  }, [registerForSiteDimensionsUpdates]);

  const dimensions = !props.isResponsiveEditor
    ? getClassicEditorDimensions({ siteWidth, siteScale })
    : getResponsiveEditorDimensions();
  return (
    <div
      data-aid="canvas-overlay-container"
      style={{
        pointerEvents: 'none',
        height: siteHeight,
        position: 'absolute',
        top: 0,
        ...dimensions,
      }}
    >
      {overlays.map(({ appDefinitionId, url, visible }) => (
        <CanvasOverlay
          key={`${appDefinitionId}-overlay`}
          appDefinitionId={appDefinitionId}
          visible={visible}
          url={url}
        />
      ))}
    </div>
  );
};

export const getCanvasOverlayContainer = (
  registerForOverlaysUpdates: (cb: ICanvasOverlayCallback) => void,
  registerForSiteDimensionsUpdates: (
    cb: (params: {
      isMobileView: boolean;
      width: number;
      height: number;
      siteScale: number;
    }) => void,
  ) => void,
  isResponsiveEditor: boolean,
) => (
  <CanvasOverlayContainer
    isResponsiveEditor={isResponsiveEditor}
    registerForOverlayUpdates={registerForOverlaysUpdates}
    registerForSiteDimensionsUpdates={registerForSiteDimensionsUpdates}
  />
);
